import React, { type Ref } from 'react';

import { SearchModes } from '@/domainLogic/index.ts';
import { useSearchPageData } from '@/app/components/search/searchPageDataProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { type GoToRoute } from '@/app/components/search/pages/indexClient.tsx';
import type { SearchParams } from '@/server/searchParams.ts';

import { createQueryString } from '@/utils/queryUtils.ts';
import Adv from '@/app/common/biff/searchPage/AdvSearchPage';
import OpenXCityCard from './OpenXCityCard';
import clickEvent from '../../Curiosity/clickEvent';
import { Actions, Categories, InteractionLabel } from '../../Analytics/utils';
import { analyticsUserInteraction } from '../../Analytics/analyticsUserInteraction';

import { CARDS_BEFORE_BANNER, CityCardsGridStyled } from './styled';
import { useIs1240orGreater, useIsMobile } from '../../utils/useSsrMedia';
import type { RouteType } from '../../Curiosity';
import type { CityCard } from '../../utils/buildCityCards/cityCardTypes';
import type { ClickParams } from '../../Curiosity/Analytics/utils';

export type CtaCallBakParams = {
  payload: ClickParams;
  newSearchParams: SearchParams;
};
export type CtaCallback = (params: CtaCallBakParams) => void;

export type CityCardsGridProps = {
  cityCards: CityCard[];
  routeType: RouteType;
  hide: boolean;
  cardsGridRef: React.MutableRefObject<HTMLDivElement | undefined>;
  goToRoute: GoToRoute;
};

const CityCardsGrid = ({ cityCards, routeType, hide, cardsGridRef, goToRoute }: CityCardsGridProps) => {
  const { searchInfoData, searchInfoDetailData, commonData, curiosityParams, localization, searchParams } =
    usePageWrapperData();
  const { isLoading } = useSearchPageData();
  const { searchMode } = searchInfoData;
  const { isMobileDevice, isDesktopDevice } = commonData;
  const isMobile = useIsMobile(isMobileDevice);
  const isBigDesktop = useIs1240orGreater(isDesktopDevice);

  const ctaCallBack = ({ payload, newSearchParams }: CtaCallBakParams) => {
    if (payload) {
      clickEvent({ clickParams: payload, routeType, commonData, localization, curiosityParams });
    }

    goToRoute(newSearchParams);
    const action = searchMode === SearchModes.DP ? Actions.cardDP : Actions.cardHO;
    analyticsUserInteraction({
      searchParams,
      category: Categories.resultsList,
      action,
      label: InteractionLabel.click,
      searchInfoDetailData,
    });
  };

  if (hide) return null;

  const cardsBeforeBanner = isMobile
    ? CARDS_BEFORE_BANNER.mobile
    : isBigDesktop
      ? CARDS_BEFORE_BANNER.bigDesktop
      : CARDS_BEFORE_BANNER.tablet;
  const showBanner = (cityCards?.length || 0) >= cardsBeforeBanner;

  return (
    <CityCardsGridStyled ref={cardsGridRef as Ref<HTMLDivElement>}>
      {cityCards?.map((cityCard, i) =>
        cityCard ? (
          <OpenXCityCard
            key={createQueryString(cityCard.newSearchParams)}
            cardPosition={i}
            {...{
              cityCard,
              ctaCallBack,
              isLoading,
            }}
          />
        ) : null
      )}
      {showBanner && (
        <div className="openx-adv-banner">
          <Adv id="div-gpt-ad-top-3" isTop={true} hide={false} />
        </div>
      )}
    </CityCardsGridStyled>
  );
};

export default CityCardsGrid;
